import React, { useState } from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import {
  MainHeader,
  Body,
  SecondaryBody,
  TertiaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  PrimaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { BackButton } from '@grnhse/seedling/lib/birch/components/breadcrumbs/back';
import {
  standardRequestWithErrorFlash,
  getErrorTextFromResponse,
} from 'shared/utils/request';
import { flashError } from 'flash/flash_manager';
import { useDocumentTitle } from 'shared/utils/react_router_helper';

import CodeInput from './components/code_input';

const t = withLocalePrefix('ai_opt_out');

type Props = {
  jobPostId: number;
  organizationId: number;
  organizationName: string;
  hiringPlanName: string;
  body: string;
  submitPath: string;
  confirmPath: string;
  logo: string;
  optOutEnabled: boolean;
};

const dropdownOptions = [
  { value: 'opt_in', label: t('labels.opt_in') },
  { value: 'opt_out', label: t('labels.opt_out') },
];

const Test = ({
  organizationName,
  hiringPlanName,
  body,
  submitPath,
  confirmPath,
  logo,
  optOutEnabled,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState<Item>(
    dropdownOptions[0]
  );
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [changeMode, setChangeMode] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [token, setToken] = useState('');

  useDocumentTitle(t('header'));

  const orgAndHiringPlanName = `${organizationName} · ${hiringPlanName}`;

  const onSubmit = async () => {
    if (!email) {
      return;
    }
    try {
      const { data } = await standardRequestWithErrorFlash.post(submitPath, {
        email,
        opt_out: selectedOption.value === 'opt_out',
      });
      setToken(data.token);
      setIsSubmitted(true);
    } catch (e: unknown) {
      flashError(getErrorTextFromResponse(e));
      setIsSubmitted(false);
    }
  };

  const onConfirm = async () => {
    if (!securityCode) {
      return;
    }
    try {
      await standardRequestWithErrorFlash.post(confirmPath, {
        token,
        security_code: securityCode,
        email: email,
      });
      setIsSubmitted(false);
      setChangeMode(false);
    } catch (e: unknown) {
      flashError(getErrorTextFromResponse(e));
      setIsSubmitted(false);
    }
  };

  const onSelectBack = () => {
    setIsSubmitted(false);
    setChangeMode(false);
    setSecurityCode('');
    setEmail('');
  };

  return (
    <Stack spacing={2}>
      {logo && <img src={logo} alt="_logo" height="80" width="80" />}
      <SecondaryBody medium>{orgAndHiringPlanName}</SecondaryBody>
      <MainHeader>{t('header')}</MainHeader>
      {body && <Body dangerouslySetInnerHTML={{ __html: body }} />}
      <Divider />
      {optOutEnabled && (
        <Stack alignItems="flex-start" spacing={1}>
          {!changeMode && (
            <TertiaryButton onClick={() => setChangeMode(true)}>
              {t('change_settings')}
            </TertiaryButton>
          )}
          {changeMode && (
            <>
              <SecondaryBody medium>{t('change_settings')}</SecondaryBody>
              <TertiaryBody>{t('description')}</TertiaryBody>
            </>
          )}
        </Stack>
      )}
      <Stack direction="row" spacing={1}>
        {!isSubmitted && changeMode && (
          <>
            <SimpleSingleDropdown
              label={t('labels.match_score')}
              ariaLabelledBy={t('labels.match_score')}
              items={dropdownOptions}
              selectedItem={selectedOption}
              onSelectedItemChange={setSelectedOption}
              clearable={false}
            />
            <TextFieldWrapper>
              <TextInput
                label={t('labels.email')}
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                required
              />
            </TextFieldWrapper>
            <SubmitButton onClick={onSubmit} disabled={!email}>
              {t('labels.send')}
            </SubmitButton>
          </>
        )}

        {isSubmitted && (
          <Stack spacing={1}>
            <Stack spacing={1} alignItems="flex-start">
              <BackButton onClick={onSelectBack}>{t('labels.back')}</BackButton>
              <Body
                dangerouslySetInnerHTML={{ __html: t('enter_code', { email }) }}
              />
            </Stack>

            <Stack direction="row">
              <SecurityCodeWrapper>
                <CodeInput hasError={false} onChange={setSecurityCode} />
              </SecurityCodeWrapper>
              <SubmitButton onClick={onConfirm} disabled={!securityCode}>
                {t('labels.confirm')}
              </SubmitButton>
            </Stack>
            <TertiaryBody>{t('waiting')}</TertiaryBody>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const TextFieldWrapper = styled.div`
  width: 300px;
`;

const SubmitButton = styled(PrimaryButton)`
  margin-top: 8px;
`;

const SecurityCodeWrapper = styled.div`
  input {
    max-width: 50px;
    height: 60px;
    border: 1px solid colors.$lighter-gray;
    border-radius: 10px;
    text-align: center;
    box-sizing: border-box;
    font-size: 16px; // prevent auto zoom-in on mobile
  }

  &__wrapper {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &--error input {
    border-color: colors.$error-red !important;
  }

  .helper-text--error {
    margin-left: 0;
    margin-top: 8px;
  }
`;

export default Test;
